/* 2009-2019 Copyright Ridecell Inc. All rights reserved. */
import moment from 'moment'
import { ensureIsMutable } from '../../helpers'

/**
 * @description
 * A validation node has a validate method that takes a single value and returns
 * a validation result or undefined if the validation is successful. Usually the
 * result is a string if there's an error.
 *
 * See the end of this file to view available validation helper methods.
 *
 * @typedef Validation
 * @property {function(any): ValidationResult?} validate
 */

function isSuccess(result) {
  return (
    result === undefined ||
    (typeof result === 'object' && result !== null && Object.keys(result).length === 0)
  )
}

function isError(result) {
  return !isSuccess(result)
}

function unwrapValue(value) {
  if (typeof value === 'function') {
    return value()
  }
  return value
}

export class Validation {
  constructor() {
    this.errorMessage = null
  }

  validate(value) {
    this.value = ensureIsMutable(value)
  }

  clone(newProps) {
    const copy = Object.create(this.constructor.prototype)
    Object.assign(copy, this, newProps)
    return copy
  }

  explain(errorMessage) {
    return this.clone({ errorMessage })
  }

  error(message) {
    if (isError(message)) {
      return this.errorMessage || message
    }
    return undefined
  }
}

class ShapeValidation extends Validation {
  constructor(keys) {
    super()
    this.keys = keys
    this.allowUnknownFields = true
  }

  strict() {
    return this.clone({ allowUnknownFields: false })
  }

  validate(value) {
    super.validate(value)
    if (typeof this.value !== 'object' || this.value === null) {
      const message = Validation.messages
        ? Validation.messages.validateObjectType()
        : 'Must be an object.'
      return {
        _error: this.error(message),
      }
    }
    if (!this.allowUnknownFields) {
      const error = Object.keys(this.value).reduce((key, acc) => {
        if (!this.keys[key]) {
          const unknownMessage = Validation.messages
            ? Validation.messages.validateUnknownType(key)
            : `Field ${key} is unknown.`
          return {
            _error: this.error(unknownMessage),
          }
        }
        return acc
      }, undefined)
      if (error) {
        return error
      }
    }
    const errors = {}
    Object.keys(this.keys).forEach(key => {
      const result = this.keys[key].validate(this.value[key])
      if (isError(result)) {
        errors[key] = result
      }
    })
    return errors
  }
}

class ArrayValidation extends Validation {
  constructor(item) {
    super()
    this.item = item
    this.lowerLimit = null
    this.upperLimit = null
    this.requiredLength = null
  }

  min(lowerLimit) {
    return this.clone({ lowerLimit })
  }

  max(upperLimit) {
    return this.clone({ upperLimit })
  }

  length(requiredLength) {
    return this.clone({ requiredLength })
  }

  validate(value) {
    super.validate(value)
    let message = null

    if (!Array.isArray(this.value)) {
      message = Validation.messages ? Validation.messages.validateArrayType() : 'Must be an array.'
      return this.error(message)
    }
    if (this.lowerLimit !== null && this.value.length < this.lowerLimit) {
      message = Validation.messages
        ? Validation.messages.validateArrayLengthLower(this.lowerLimit)
        : `Must have at least ${this.lowerLimit} items.`
      return this.error(message)
    }
    if (this.upperLimit !== null && this.value.length > this.upperLimit) {
      message = Validation.messages
        ? Validation.messages.validateArrayLengthUpper(this.upperLimit)
        : `Must have no more than ${this.upperLimit} items.`
      return this.error()
    }
    for (let i = 0; i < this.value.length; i += 1) {
      const result = this.item.validate(this.value[i])
      if (isError(result)) {
        return this.error(result)
      }
    }
    return undefined
  }
}

class StringValidation extends Validation {
  constructor() {
    super()
    this.allowEmptyString = true
    this.allowWhiteSpaces = true
    this.microValidator = null
    this.maxLength = null
    this.minLength = null
    this.pattern = null
  }

  nonEmpty() {
    return this.clone({ allowEmptyString: false })
  }

  noWhiteSpaces() {
    return this.clone({ allowWhiteSpaces: false })
  }

  time() {
    const pattern = /^[0-9]{2}:[0-9]{2}$/
    const message = Validation.messages
      ? Validation.messages.validateTime()
      : 'Must be a valid time (HH:mm).'
    const microValidator = value => {
      if (!pattern.test(value)) {
        return this.error(message)
      }
      let [hours, minutes] = value.split(':')
      hours = parseInt(hours, 10)
      minutes = parseInt(minutes, 10)
      if (hours > 23 || minutes > 59) {
        return this.error(message)
      }
      return undefined
    }
    return this.clone({ microValidator })
  }

  email() {
    const message = Validation.messages
      ? Validation.messages.validateEmail()
      : 'Must be a valid email.'
    return this.match(
      /^(([^<>()[\]\\.,:\s@"]+(\.[^<>()[\]\\.,:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
    ).explain(message)
  }

  date() {
    return this.clone({
      microValidator: value => {
        if (!moment(value, 'YYYY-MM-DD').isValid()) {
          const message = Validation.messages
            ? Validation.messages.validateDate()
            : 'Must be a valid date.'
          return this.error(message)
        }
        return undefined
      },
    })
  }

  password(params) {
    const defaultparams = {
      lowercase: true,
      maxLength: 25,
      minLength: 8,
      number: true,
      special: false,
      uppercase: true,
    }
    const { lowercase, uppercase, number, special, minLength, maxLength } = Object.assign(
      defaultparams,
      params
    )
    const lowercaseMessage = Validation.messages
      ? Validation.messages.validatePasswordLowercase()
      : 'Lowercase'
    const uppercaseMessage = Validation.messages
      ? Validation.messages.validatePasswordUppercase()
      : 'Uppercase'
    const numberMessage = Validation.messages
      ? Validation.messages.validatePasswordNumber()
      : 'Number'
    const specialMessage = Validation.messages
      ? Validation.messages.validatePasswordSpecial()
      : 'Special'

    return this.clone({
      maxLength,
      microValidator: value => {
        const needsLowercase = lowercase && !/(?=.*[a-z])/.test(value) && lowercaseMessage
        const needsUppercase = uppercase && !/(?=.*[A-Z])/.test(value) && uppercaseMessage
        const needsNumber = number && !/(?=.*\d)/.test(value) && numberMessage
        const needsSpecial = special && !/(?=.*[!@#$%^&*])/.test(value) && specialMessage
        const missing = [needsLowercase, needsUppercase, needsNumber, needsSpecial]
          .filter(n => n)
          .join(', ')
        if (missing) {
          const missingMessage = Validation.messages
            ? Validation.messages.validatePassword(missing)
            : `Missing: ${missing}`
          return this.error(missingMessage)
        }
        return undefined
      },
      minLength,
    })
  }

  phoneNumber() {
    const message = Validation.messages
      ? Validation.messages.validatePhoneNumber()
      : 'Must be a valid phone number.'
    return (
      this
        // eslint-disable-next-line no-useless-escape
        .match(/^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/im)
        .explain(message)
    )
  }

  max(maxLength) {
    return this.clone({ maxLength })
  }

  min(minLength) {
    return this.clone({ minLength })
  }

  match(pattern) {
    return this.clone({ pattern })
  }

  validate(value) {
    super.validate(value)
    let message = null

    if (!this.allowEmptyString && !this.value) {
      message = Validation.messages ? Validation.messages.validateInputRequired() : 'Required.'
      return this.error(message)
    }
    if (!this.allowWhiteSpaces && /\s/.test(this.value)) {
      message = Validation.messages
        ? Validation.messages.validateInputSpaces()
        : 'Must not have white spaces.'
      return this.error(message)
    }
    if (typeof this.value !== 'string') {
      message = Validation.messages ? Validation.messages.validateStringType() : 'Must be a string.'
      return this.error(message)
    }
    if (this.minLength !== null && this.value.length < this.minLength) {
      message = Validation.messages
        ? Validation.messages.validateMinChars(this.minLength)
        : `Must have at least ${this.minLength} characters.`
      return this.error(message)
    }
    if (this.maxLength !== null && this.value.length > this.maxLength) {
      message = Validation.messages
        ? Validation.messages.validateMaxChars(this.maxLength)
        : `Must not have more than ${this.maxLength} characters.`
      return this.error(message)
    }
    if (this.pattern !== null && !this.pattern.test(this.value)) {
      message = Validation.messages
        ? Validation.messages.validatePattern(this.pattern.toString())
        : `Must match ${this.pattern.toString()}.`
      return this.error(message)
    }
    if (this.microValidator) {
      return this.error(this.microValidator(this.value))
    }
    return undefined
  }
}

class NumberValidation extends Validation {
  constructor() {
    super()
    this.maxDecimalPlaces = null
  }

  places(maxDecimalPlaces) {
    return this.clone({ maxDecimalPlaces })
  }

  validate(value) {
    super.validate(value)
    if (typeof this.value !== 'number') {
      const numberMessage = Validation.messages
        ? Validation.messages.validateNumber()
        : 'Must be a number.'
      return this.error(numberMessage)
    }
    if (this.maxDecimalPlaces !== null) {
      const text = this.value.toString()
      const decimal = text.split('.')[1]
      if (decimal && decimal.length > this.maxDecimalPlaces) {
        const decimalMessage = Validation.messages
          ? Validation.messages.validateDecimals(this.maxDecimalPlaces)
          : `Must not have more than ${this.maxDecimalPlaces} decimal places.`
        return this.error(decimalMessage)
      }
    }
    return undefined
  }
}

class BooleanValidation extends Validation {
  validate(value) {
    super.validate(value)
    if (typeof this.value !== 'boolean') {
      const message = Validation.messages
        ? Validation.messages.validateBoolean()
        : 'Must be a boolean.'
      return this.error(message)
    }
    return undefined
  }
}

class DateValidation extends Validation {
  validate(value) {
    super.validate(value)
    const message = Validation.messages
      ? Validation.messages.validateDate()
      : 'Must be a valid date.'
    if (!(this.value instanceof Date)) {
      return this.error(message)
    }
    if (isNaN(this.value)) {
      return this.error(message)
    }
    return undefined
  }
}

class MomentValidation extends Validation {
  validate = value => {
    super.validate(value)
    const message = Validation.messages
      ? Validation.messages.validateDate()
      : 'Must be a valid date.'
    return !moment.isMoment(this.value) ? this.error(message) : undefined
  }
}

class OptionalValidation extends Validation {
  constructor(innerValidation) {
    super()
    this.innerValidation = innerValidation
  }

  validate(value) {
    super.validate(value)
    if (this.value !== undefined && this.value !== null) {
      return this.error(this.innerValidation.validate(this.value))
    }
    return undefined
  }
}

class NullableValidation extends Validation {
  constructor(innerValidation) {
    super()
    this.innerValidation = innerValidation
  }

  validate(value) {
    super.validate(value)
    if (this.value !== null) {
      return this.error(this.innerValidation.validate(this.value))
    }
    return undefined
  }
}

class AndValidation extends Validation {
  constructor(...validations) {
    super()
    this.validations = validations
  }

  validate(value) {
    super.validate(value)
    const options = []
    const everyPassed = this.validations.every(validation => {
      const result = validation.validate(this.value)
      if (isSuccess(result)) {
        return true
      }
      options.push(result)
      return false
    })
    if (everyPassed) {
      return {}
    }
    return this.error(options[0])
  }
}

class OrValidation extends Validation {
  constructor(...validations) {
    super()
    this.validations = validations
  }

  validate(value) {
    super.validate(value)
    const options = []
    const anyPassed = this.validations.some(validation => {
      const result = validation.validate(this.value)
      if (isSuccess(result)) {
        return true
      }
      options.push(result)
      return false
    })
    if (anyPassed) {
      return {}
    }
    return this.error(options.join(' '))
  }
}

class GreaterThanOrEqualToValidation extends Validation {
  constructor(compareValue) {
    super()
    this.compareValue = compareValue
  }

  validate(value) {
    super.validate(value)
    const compareValue = unwrapValue(this.compareValue)
    if (this.value < compareValue) {
      const message = Validation.messages
        ? Validation.messages.validateGreaterEqual(compareValue)
        : `Must be at least ${this.compareValue}.`
      return this.error(message)
    }
    return undefined
  }
}

class GreaterThanValidation extends Validation {
  constructor(compareValue) {
    super()
    this.compareValue = compareValue
  }

  validate(value) {
    super.validate(value)
    const compareValue = unwrapValue(this.compareValue)
    if (this.value <= compareValue) {
      const message = Validation.messages
        ? Validation.messages.validateGreater(compareValue)
        : `Must be greater than ${this.compareValue}.`
      return this.error(message)
    }
    return undefined
  }
}

class EqualToValidation extends Validation {
  constructor(compareValue) {
    super()
    this.compareValue = compareValue
  }

  validate(value) {
    super.validate(value)
    const compareValue = unwrapValue(this.compareValue)
    if (this.value !== compareValue) {
      const message = Validation.messages
        ? Validation.messages.validateEqual(compareValue)
        : `Must be equal to ${this.compareValue}.`
      return this.error(message)
    }
    return undefined
  }
}

class LessThanOrEqualToValidation extends Validation {
  constructor(compareValue) {
    super()
    this.compareValue = compareValue
  }

  validate(value) {
    super.validate(value)
    const compareValue = unwrapValue(this.compareValue)
    if (this.value > compareValue) {
      const message = Validation.messages
        ? Validation.messages.validateLessEqual(compareValue)
        : `Must not be greater than ${this.compareValue}`
      return this.error(message)
    }
    return undefined
  }
}

class LessThanValidation extends Validation {
  constructor(compareValue) {
    super()
    this.compareValue = compareValue
  }

  validate(value) {
    super.validate(value)
    const compareValue = unwrapValue(this.compareValue)
    if (this.value >= compareValue) {
      const message = Validation.messages
        ? Validation.messages.validateLess(compareValue)
        : `Must be less than ${this.compareValue}.`
      return this.error(message)
    }
    return undefined
  }
}

class BetweenValidation extends Validation {
  constructor(minValue, maxValue) {
    super()
    this.minValue = minValue
    this.maxValue = maxValue
  }

  validate(value) {
    super.validate(value)
    if (this.value < this.minValue || this.value > this.maxValue) {
      const message = Validation.messages
        ? Validation.messages.validateBetween({ maxValue: this.maxValue, minValue: this.minValue })
        : `Must be between ${this.minValue} and ${this.maxValue}.`
      return this.error(message)
    }
    return undefined
  }
}

class RequiredValidation extends Validation {
  validate(value) {
    super.validate(value)
    if (this.value === null || this.value === undefined) {
      const message = Validation.messages
        ? Validation.messages.validateInputRequired()
        : 'Required.'
      return this.error(message)
    }
    return undefined
  }
}

class InstanceOfValidation extends Validation {
  constructor(Class) {
    super()
    this.Class = Class
  }

  validate(value) {
    super.validate(value)
    if (!(this.value instanceof this.Class)) {
      const message = Validation.messages
        ? Validation.messages.validateInstance(this.Class.name)
        : `Must be a ${this.Class.name}.`
      return this.error(message)
    }
    return undefined
  }
}

class CustomValidation extends Validation {
  constructor(callback) {
    super()
    this.callback = callback
  }

  validate(value) {
    super.validate(value)
    if (!this.callback(this.value)) {
      const message = Validation.messages ? Validation.messages.validateInvalid() : 'Invalid.'
      return this.error(message)
    }
    return undefined
  }
}

class ConditionalValidation extends Validation {
  constructor(condition, validation) {
    super()
    this.condition = condition
    this.validation = validation
  }

  validate(value) {
    super.validate(value)
    if (this.condition(this.value)) {
      return this.error(this.validation.validate(this.value))
    }
    return {}
  }
}

class OptionsValidation extends Validation {
  constructor(...options) {
    super()
    this.options = options
  }

  validate(value) {
    super.validate(value)
    if (this.options.indexOf(this.value) === -1) {
      const message = Validation.messages
        ? Validation.messages.validateOptions(this.options.join(', '))
        : `Must be one of ${this.options.join(', ')}.`
      return this.error(message)
    }
    return undefined
  }
}

Object.assign(Validation, {
  and: (...validations) => new AndValidation(...validations), // all validations must be successful
  arrayOf: validation => new ArrayValidation(validation), // an array
  between: (minValue, maxValue) => new BetweenValidation(minValue, maxValue), // between (>= && <=)
  bool: () => new BooleanValidation(), // a boolean
  custom: callback => new CustomValidation(callback), // custom validator function
  date: () => new DateValidation(), // a date instance
  eq: compareValue => new EqualToValidation(compareValue), // equals (===)
  gt: compareValue => new GreaterThanValidation(compareValue), // greater than
  gte: compareValue => new GreaterThanOrEqualToValidation(compareValue), // greater than or equal to
  instanceOf: Class => new InstanceOfValidation(Class), // instance
  lt: compareValue => new LessThanValidation(compareValue), // less than
  lte: compareValue => new LessThanOrEqualToValidation(compareValue), // less than or equal to
  moment: () => new MomentValidation(), // a moment object
  nullableOf: validation => new NullableValidation(validation), // can be null
  number: () => new NumberValidation(), // a number
  oneOf: (...options) => new OptionsValidation(...options), // value must be one of the specified options
  optionalOf: validation => new OptionalValidation(validation), // can be null or undefined
  or: (...validations) => new OrValidation(...validations), // any successful validation will pass
  required: () => new RequiredValidation(), // not null and not undefined
  shape: keys => new ShapeValidation(keys), // a shape
  string: () => new StringValidation(), // a string
  whenThen: (condition, validation) => new ConditionalValidation(condition, validation), // conditional validation
})

Object.assign(Validation, {
  coordinate: () => Validation.number().places(8),
  currency: () => Validation.and(Validation.number().places(2), Validation.gte(0)),
  file: () => Validation.or(Validation.string().nonEmpty(), Validation.instanceOf(File)),
  id: () => Validation.and(Validation.number().places(0), Validation.gte(0)),
  integer: () => Validation.number().places(0),
  nonZeroCurrency: () => Validation.and(Validation.currency(), Validation.gt(0)),
  nonZeroPositiveInteger: () => Validation.and(Validation.positiveInteger(), Validation.gt(0)),
  percentage: () =>
    Validation.and(Validation.number(), Validation.gte(0), Validation.lte(100)).explain(
      'Must be between 0 and 100.'
    ),
  positiveFloat: () => Validation.and(Validation.number(), Validation.gte(0)),
  positiveInteger: () => Validation.and(Validation.integer(), Validation.gte(0)),
})

export default Validation
